import { getUriQueryParameter } from '@impuls-coach/util-get-uri-query-parameter';
import { preventedTransitions } from 'defs';
import { EventCustomFeedbackCodes, EventFeedbackCodes, EventLevels, EventTypes } from 'defs/events';
import { getRouteHref, PreventedTransitionType } from 'defs/routes';
import { actionCreators as eventsAC } from 'store/events';
import { IRootState } from 'store/rootReducer';

export type StoreType = {
	getState: () => IRootState;
	dispatch: (event: Object) => void;
};

export type WindowLocationType = {
	href: string;
};

export type LocationType = {
	pathname: string;
};

export function isPathNamePrevented(pathName: string, prevention: Array<string>): boolean {
	let preventCounter: number = 0;

	prevention.forEach((prevent: string) => {
		const reg: RegExp = new RegExp(`${prevent.replace(/:[\w|\d]*/g, '[\\d|\\w]*')}$`);
		const matched = pathName.match(reg);
		if (matched && matched !== null && matched.length >= 0) {
			preventCounter += 1;
		}
	});

	return preventCounter > 0;
}

export function isTransitionPrevented(
	fromPathName: string,
	toPathName: string,
	preventedTransitionsArg: PreventedTransitionType,
	store: StoreType
): boolean {
	if (typeof preventedTransitionsArg.middleware === 'function') {
		const middlewareResult: boolean | null = preventedTransitionsArg.middleware(store, fromPathName, toPathName);

		if (typeof middlewareResult === 'boolean') {
			return middlewareResult;
		}
	}

	return (
		isPathNamePrevented(fromPathName, preventedTransitionsArg.from) &&
		isPathNamePrevented(toPathName, preventedTransitionsArg.to)
	);
}

/**
 * @param { dispatch: Function } store - the store
 * @param { string } fromPathName - from pathname
 * @param { string } toPathName - to pathname
 * @return { boolean } allowTransition
 */
const historyMiddleware = (store: StoreType, fromPathName?: string, toPathName?: string): boolean => {
	if (typeof fromPathName !== 'string' || typeof toPathName !== 'string') {
		return true;
	}

	// allow to leave when url is referred PULSWM-3720 & PULSWM-3730
	const referred = getUriQueryParameter('referred');
	if (
		typeof referred === 'string' &&
		referred === 'true' &&
		// allow just selectDietaryHabits and selectActivityLevel
		isPathNamePrevented(fromPathName, [
			getRouteHref('anamnesis.selectDietaryHabits'),
			getRouteHref('anamnesis.selectActivityLevel'),
			getRouteHref('anamnesis.dsgvo'),
		])
	) {
		return true;
	}

	const isAnamnesisLeavePrevented = isTransitionPrevented(
		fromPathName,
		toPathName,
		preventedTransitions.anamnesis,
		store
	);

	if (isAnamnesisLeavePrevented) {
		store.dispatch(
			eventsAC.doAddEvents([
				{
					eventLevel: EventLevels.NotAvailable,
					code: EventFeedbackCodes.F12,
					eventType: EventTypes.Feedback,
				},
			] as any)
		);
	}

	const isLoginLeavePrevented: boolean = isTransitionPrevented(
		fromPathName,
		toPathName,
		preventedTransitions.login,
		store
	);
	if (isLoginLeavePrevented) {
		store.dispatch(
			eventsAC.doAddEvents([
				{
					eventLevel: EventLevels.NotAvailable,
					code: EventCustomFeedbackCodes.LoginLeaving,
					eventType: EventTypes.Feedback,
				},
			] as any)
		);
	}
	return !(isAnamnesisLeavePrevented || isLoginLeavePrevented);
};

export default historyMiddleware;
